const EARTH_RADIUS_M = 6371000;
/** WGS84 semi-major axis — matches Leaflet / Maps Lead Extraction POC bounds helper. */
const WGS84_SEMI_MAJOR_AXIS_M = 6378137;

/**
 * @param {unknown} value
 * @returns {number}
 */
function toFiniteNumber(value) {
    if (value === null || value === undefined || value === '') {
        return NaN;
    }
    const n = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(n) ? n : NaN;
}

/**
 * Great-circle distance in metres between two WGS84 points (spherical law of cosines).
 * Matches the legacy ForceManager map helper: degrees → nautical miles × 1.609344 km × 1000,
 * then floored to an integer metre value.
 *
 * @param {unknown} lat1 Latitude of point A in decimal degrees
 * @param {unknown} lon1 Longitude of point A in decimal degrees
 * @param {unknown} lat2 Latitude of point B in decimal degrees
 * @param {unknown} lon2 Longitude of point B in decimal degrees
 * @returns {number|null} Distance in metres, or `null` if any coordinate is not a finite number
 */
export function getDistanceInMeters(lat1, lon1, lat2, lon2) {
    const a = toFiniteNumber(lat1);
    const b = toFiniteNumber(lon1);
    const c = toFiniteNumber(lat2);
    const d = toFiniteNumber(lon2);
    if (![a, b, c, d].every(Number.isFinite)) {
        return null;
    }

    const radlat1 = (Math.PI * a) / 180;
    const radlat2 = (Math.PI * c) / 180;
    const radtheta = (Math.PI * (b - d)) / 180;
    let cosDist =
        Math.sin(radlat1) * Math.sin(radlat2) +
        Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
    cosDist = Math.min(1, Math.max(-1, cosDist));
    let dist = Math.acos(cosDist);
    dist = (dist * 180) / Math.PI;
    dist = dist * 60 * 1.1515 * 1.609344 * 1000;
    return Math.floor(dist);
}

/**
 * @param {number} degrees
 * @returns {number}
 */
function toRadians(degrees) {
    return (degrees * Math.PI) / 180;
}

/**
 * @param {number} radians
 * @returns {number}
 */
function toDegrees(radians) {
    return (radians * 180) / Math.PI;
}

/**
 * Great-circle destination from a WGS84 point, bearing, and distance.
 *
 * @param {unknown} lat Latitude in decimal degrees
 * @param {unknown} lon Longitude in decimal degrees
 * @param {unknown} bearingDeg Bearing in degrees clockwise from north
 * @param {unknown} distanceM Distance in metres
 * @returns {{ latitude: number, longitude: number } | null}
 */
export function destinationPoint(lat, lon, bearingDeg, distanceM) {
    const lat1 = toFiniteNumber(lat);
    const lon1 = toFiniteNumber(lon);
    const bearing = toFiniteNumber(bearingDeg);
    const distance = toFiniteNumber(distanceM);
    if (![lat1, lon1, bearing, distance].every(Number.isFinite) || distance < 0) {
        return null;
    }

    const angularDistance = distance / EARTH_RADIUS_M;
    const lat1Rad = toRadians(lat1);
    const lon1Rad = toRadians(lon1);
    const bearingRad = toRadians(bearing);

    const lat2Rad = Math.asin(
        Math.sin(lat1Rad) * Math.cos(angularDistance) +
            Math.cos(lat1Rad) * Math.sin(angularDistance) * Math.cos(bearingRad),
    );
    const lon2Rad =
        lon1Rad +
        Math.atan2(
            Math.sin(bearingRad) * Math.sin(angularDistance) * Math.cos(lat1Rad),
            Math.cos(angularDistance) - Math.sin(lat1Rad) * Math.sin(lat2Rad),
        );

    return {
        latitude: toDegrees(lat2Rad),
        longitude: toDegrees(lon2Rad),
    };
}

/**
 * Internal helper: axis-aligned rectangle from centre ± radius in cardinal directions.
 */
function getAxisAlignedRectangleFromRadius(centerLat, centerLon, radiusMeters) {
    const lat = toFiniteNumber(centerLat);
    const lon = toFiniteNumber(centerLon);
    const radius = toFiniteNumber(radiusMeters);
    if (![lat, lon, radius].every(Number.isFinite) || radius <= 0) {
        return null;
    }

    const phi = (lat * Math.PI) / 180;
    const deltaLat = (radius / WGS84_SEMI_MAJOR_AXIS_M) * (180 / Math.PI);
    const cosPhi = Math.cos(phi);
    const deltaLng =
        cosPhi === 0
            ? 0
            : (radius / (WGS84_SEMI_MAJOR_AXIS_M * Math.abs(cosPhi))) * (180 / Math.PI);

    return {
        low: { latitude: lat - deltaLat, longitude: lon - deltaLng },
        high: { latitude: lat + deltaLat, longitude: lon + deltaLng },
    };
}

/**
 * Axis-aligned bounding rectangle that fully contains a circle of the given radius
 * around a centre point (circumscribed square). Matches the Maps Lead Extraction
 * `GeoRectangle` shape (`low` = southwest, `high` = northeast).
 *
 * @param {unknown} centerLat Centre latitude in decimal degrees
 * @param {unknown} centerLon Centre longitude in decimal degrees
 * @param {unknown} radiusMeters Circle radius in metres
 * @returns {{ low: { latitude: number, longitude: number }, high: { latitude: number, longitude: number } } | null}
 */
export function getBoundingRectangleFromRadius(centerLat, centerLon, radiusMeters) {
    return getAxisAlignedRectangleFromRadius(centerLat, centerLon, radiusMeters);
}

/**
 * Largest axis-aligned square fully inside a circle of the given radius around a centre
 * point (inscribed square). Each edge is at distance `radius / √2` from the centre.
 *
 * @param {unknown} centerLat Centre latitude in decimal degrees
 * @param {unknown} centerLon Centre longitude in decimal degrees
 * @param {unknown} radiusMeters Circle radius in metres
 * @returns {{ low: { latitude: number, longitude: number }, high: { latitude: number, longitude: number } } | null}
 */
export function getInscribedRectangleFromRadius(centerLat, centerLon, radiusMeters) {
    const radius = toFiniteNumber(radiusMeters);
    if (!Number.isFinite(radius) || radius <= 0) {
        return null;
    }

    return getAxisAlignedRectangleFromRadius(centerLat, centerLon, radius / Math.SQRT2);
}
